// AdminAppUsersPage — Phase 2 of the unified back-office.
// Consumes /api/admin/app-bridge/users (proxied to app.geovinum.io).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users, Search, Download, RefreshCw, MoreVertical, Ban, CheckCircle,
  CreditCard, Coins, Loader2, AlertCircle, X, Trash2,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const PLAN_OPTIONS = [
  { value: "", label: "Tous plans" },
  { value: "free", label: "Free" },
  { value: "amateur", label: "Amateur" },
  { value: "pro", label: "Pro" },
];

const ROLE_OPTIONS = [
  { value: "", label: "Tous rôles" },
  { value: "user", label: "Utilisateur" },
  { value: "admin", label: "Admin" },
];

/** Stable id resolver — the upstream shape is inconsistent so we normalise here. */
const uidOf = (u) => u?.id || u?._id || u?.user_id || u?.email;

const AdminAppUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [plan, setPlan] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionUser, setActionUser] = useState(null); // {user, kind}

  // Bulk selection — `selectedIds` is a Set of user ids, `lastPickedIdx`
  // is the anchor for Shift+Click range selection (à la Gmail / Finder).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastPickedIdx, setLastPickedIdx] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listAppUsers({ query, plan, role, limit: 200 });
      const list = Array.isArray(res) ? res : res?.items || res?.users || [];
      setUsers(list);
      // Any selected id that no longer exists in the fresh list is dropped.
      setSelectedIds((prev) => {
        const alive = new Set(list.map(uidOf));
        const next = new Set();
        prev.forEach((id) => { if (alive.has(id)) next.add(id); });
        return next;
      });
      setLastPickedIdx(null);
    } catch (e) {
      setError(e?.message || String(e));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [query, plan, role]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle a row-click on the checkbox. Native Shift+Click selects the
  // range from the last anchor to `idx` inclusive; a plain click toggles
  // the single row and updates the anchor.
  const toggleRow = useCallback(
    (idx, e) => {
      const id = uidOf(users[idx]);
      if (!id) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (e?.shiftKey && lastPickedIdx != null && lastPickedIdx !== idx) {
          const [start, end] = idx < lastPickedIdx ? [idx, lastPickedIdx] : [lastPickedIdx, idx];
          // Range selection mirrors the anchor's target state: if the anchor
          // is currently selected, the range is added; otherwise it's cleared.
          const anchorSelected = prev.has(uidOf(users[lastPickedIdx]));
          for (let i = start; i <= end; i += 1) {
            const rid = uidOf(users[i]);
            if (!rid) continue;
            if (anchorSelected) next.add(rid);
            else next.delete(rid);
          }
        } else if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      setLastPickedIdx(idx);
    },
    [users, lastPickedIdx],
  );

  const allSelected = users.length > 0 && users.every((u) => selectedIds.has(uidOf(u)));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleAll = () => {
    setSelectedIds(() => (allSelected ? new Set() : new Set(users.map(uidOf))));
    setLastPickedIdx(null);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    setError(null);
    try {
      const ids = Array.from(selectedIds);
      const res = await adminApi.bulkDeleteAppUsers(ids);
      // Surface partial failures — the upstream may reject e.g. admins
      // linked to active subscriptions.
      const failed = (res?.results || []).filter((r) => !r.ok);
      if (failed.length) {
        setError(
          `${res.deleted}/${res.total} suppressions réussies. ` +
          `Échecs : ${failed.map((f) => f.user_id).join(", ")}`,
        );
      }
      setSelectedIds(new Set());
      setConfirmBulk(false);
      await fetchUsers();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleExport = async (fmt) => {
    try {
      const authRaw = localStorage.getItem("geovinum.auth.v1");
      const token = authRaw ? JSON.parse(authRaw)?.token : "";
      const url = adminApi.exportAppUsersUrl(fmt);
      const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error(`Export failed: ${r.status}`);
      const blob = await r.blob();
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `app-users.${fmt}`;
      dl.click();
      setTimeout(() => URL.revokeObjectURL(dl.href), 1000);
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  return (
    <div data-testid="admin-app-users">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Utilisateurs app.geovinum.io</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchUsers}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px] hover:bg-[color:var(--gv-mist)] transition-colors"
            data-testid="app-users-refresh-btn"
          >
            <RefreshCw size={13} /> Rafraîchir
          </button>
          <button
            onClick={() => handleExport("csv")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px] hover:bg-[color:var(--gv-mist)] transition-colors"
            data-testid="app-users-export-csv-btn"
          >
            <Download size={13} /> CSV
          </button>
          <button
            onClick={() => handleExport("json")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px] hover:bg-[color:var(--gv-mist)] transition-colors"
            data-testid="app-users-export-json-btn"
          >
            <Download size={13} /> JSON
          </button>
        </div>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-6">
        Base d'utilisateurs d'app.geovinum.io — actions ban / set plan / crédit propagées via le bridge.
      </p>

      {/* Filters */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-3 mb-5 flex flex-wrap gap-2 items-center" data-testid="app-users-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Recherche email ou nom…"
            className="gv-input w-full pl-8"
            data-testid="app-users-search-input"
          />
        </div>
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="gv-input"
          data-testid="app-users-plan-filter"
        >
          {PLAN_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="gv-input"
          data-testid="app-users-role-filter"
        >
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="app-users-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium mb-0.5">Impossible de récupérer les utilisateurs</div>
            <div className="text-[12px] opacity-80">{error}</div>
            <div className="text-[11px] opacity-70 mt-1">
              Vérifiez que l'app.geovinum.io backend est en ligne (preview URL peut être suspendu).
            </div>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-[color:var(--gv-pink)]/10 border border-[color:var(--gv-pink)]/30 rounded-[5px] px-4 py-2.5 mb-3" data-testid="bulk-bar">
          <div className="text-[13px] text-[color:var(--gv-ink)]">
            <span className="font-medium">{selectedIds.size}</span> utilisateur{selectedIds.size > 1 ? "s" : ""} sélectionné{selectedIds.size > 1 ? "s" : ""}
            <span className="text-[color:var(--gv-ink)]/50 ml-2">· Astuce : Maj+clic pour sélectionner une plage</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSelectedIds(new Set()); setLastPickedIdx(null); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-[12.5px] text-[color:var(--gv-ink)]/70 hover:bg-white transition-colors"
              data-testid="bulk-clear-btn"
            >
              Annuler
            </button>
            <button
              onClick={() => setConfirmBulk(true)}
              disabled={bulkDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] bg-red-600 text-white text-[12.5px] font-medium hover:bg-red-700 transition-colors disabled:opacity-60"
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={13} /> Supprimer la sélection
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="app-users-loading">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="app-users-empty">
            <Users size={14} /> Aucun utilisateur trouvé
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--gv-mist)] text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">
              <tr>
                <th className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    aria-label="Tout sélectionner"
                    data-testid="app-users-select-all"
                    className="cursor-pointer"
                  />
                </th>
                <th className="text-left px-4 py-2.5 font-medium">Email</th>
                <th className="text-left px-4 py-2.5 font-medium">Nom</th>
                <th className="text-left px-4 py-2.5 font-medium">Plan</th>
                <th className="text-left px-4 py-2.5 font-medium">Rôle</th>
                <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                <th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <UserRow
                  key={uidOf(u) || idx}
                  user={u}
                  idx={idx}
                  selected={selectedIds.has(uidOf(u))}
                  onToggle={toggleRow}
                  onAction={setActionUser}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="text-[11.5px] text-[color:var(--gv-ink)]/50 mt-2">{users.length} utilisateur{users.length > 1 ? "s" : ""} affiché{users.length > 1 ? "s" : ""}</div>

      {actionUser && (
        <ActionModal
          user={actionUser.user}
          kind={actionUser.kind}
          onClose={() => setActionUser(null)}
          onDone={() => { setActionUser(null); fetchUsers(); }}
        />
      )}

      {confirmBulk && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !bulkDeleting && setConfirmBulk(false)}
        >
          <div
            className="bg-white rounded-[8px] w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="bulk-confirm-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-[color:var(--gv-ink)]">
                Supprimer {selectedIds.size} utilisateur{selectedIds.size > 1 ? "s" : ""} ?
              </h3>
              <button
                onClick={() => setConfirmBulk(false)}
                disabled={bulkDeleting}
                className="p-1 rounded hover:bg-[color:var(--gv-mist)]"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-[color:var(--gv-ink)]/75 mb-4">
              Cette action est <strong>irréversible</strong>. Les comptes, abonnements en
              cours, crédits et historiques seront supprimés côté app.geovinum.io.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmBulk(false)}
                disabled={bulkDeleting}
                className="px-4 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px]"
                data-testid="bulk-confirm-cancel"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 rounded-[4px] bg-red-600 text-white text-[13px] hover:bg-red-700 disabled:opacity-60"
                data-testid="bulk-confirm-submit"
              >
                {bulkDeleting ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const UserRow = ({ user, idx, selected, onToggle, onAction }) => {
  const uid = user.id || user._id || user.user_id;
  const plan = user.plan || user.subscription_plan || "free";
  const isBanned = !!user.banned;
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <tr
      className={`border-t border-[color:var(--gv-line)] transition-colors ${
        selected ? "bg-[color:var(--gv-pink)]/5" : "hover:bg-[color:var(--gv-mist)]/30"
      }`}
      data-testid={`app-user-row-${uid}`}
    >
      <td className="px-3 py-2.5 align-middle">
        <input
          type="checkbox"
          checked={!!selected}
          // Prevent the browser from firing an implicit `select` on the row
          // (the anchor row would otherwise change to whichever row the
          // native focus lands on). We pass the raw event to `onToggle` so
          // Shift+Click is detected.
          onClick={(e) => { e.stopPropagation(); onToggle(idx, e); }}
          onChange={() => {}}  // controlled — real toggle happens on click
          aria-label={`Sélectionner ${user.email || uid}`}
          className="cursor-pointer"
          data-testid={`app-user-select-${uid}`}
        />
      </td>
      <td className="px-4 py-2.5 text-[color:var(--gv-ink)]">{user.email}</td>
      <td className="px-4 py-2.5 text-[color:var(--gv-ink)]/75">{user.name || "—"}</td>
      <td className="px-4 py-2.5">
        <span className={`inline-flex px-2 py-0.5 rounded-[4px] text-[11px] font-medium ${
          plan === "pro" ? "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]" :
          plan === "amateur" ? "bg-blue-50 text-blue-700" :
          "bg-gray-100 text-gray-600"
        }`}>{plan}</span>
      </td>
      <td className="px-4 py-2.5 text-[color:var(--gv-ink)]/70">{user.role || "user"}</td>
      <td className="px-4 py-2.5">
        {isBanned ? (
          <span className="inline-flex items-center gap-1 text-[11.5px] text-red-600"><Ban size={11} /> Banni</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-600"><CheckCircle size={11} /> Actif</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="p-1 rounded hover:bg-[color:var(--gv-mist)]"
          data-testid={`app-user-actions-${uid}`}
        >
          <MoreVertical size={14} />
        </button>
        {menuOpen && (
          <>
            <button
              className="fixed inset-0 z-30 cursor-default"
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute right-4 mt-1 z-40 bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-lg py-1 min-w-[180px] text-left">
              <MenuItem icon={CreditCard} label="Changer le plan" onClick={() => { setMenuOpen(false); onAction({ user, kind: "plan" }); }} testid={`menu-plan-${uid}`} />
              <MenuItem icon={Coins} label="Ajouter des crédits" onClick={() => { setMenuOpen(false); onAction({ user, kind: "credits" }); }} testid={`menu-credits-${uid}`} />
              <MenuItem icon={Ban} label={isBanned ? "Débannir" : "Bannir"} danger={!isBanned} onClick={() => { setMenuOpen(false); onAction({ user, kind: "ban" }); }} testid={`menu-ban-${uid}`} />
              <MenuItem icon={Trash2} label="Supprimer" danger onClick={() => { setMenuOpen(false); onAction({ user, kind: "delete" }); }} testid={`menu-delete-${uid}`} />
            </div>
          </>
        )}
      </td>
    </tr>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, danger, testid }) => (
  <button
    onClick={onClick}
    className={`w-full px-3 py-2 text-left text-[13px] flex items-center gap-2 hover:bg-[color:var(--gv-mist)] ${danger ? "text-red-600" : "text-[color:var(--gv-ink)]"}`}
    data-testid={testid}
  >
    <Icon size={12} /> {label}
  </button>
);

const ActionModal = ({ user, kind, onClose, onDone }) => {
  const uid = user.id || user._id || user.user_id;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [planValue, setPlanValue] = useState(user.plan || user.subscription_plan || "amateur");
  const [months, setMonths] = useState(1);
  const [credits, setCredits] = useState(100);

  const title = useMemo(() => ({
    plan: "Modifier le plan",
    credits: "Ajouter des crédits",
    ban: user.banned ? "Débannir l'utilisateur" : "Bannir l'utilisateur",
    delete: "Supprimer l'utilisateur",
  })[kind], [kind, user.banned]);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (kind === "plan") await adminApi.setAppUserPlan(uid, planValue, months);
      else if (kind === "credits") await adminApi.creditAppUser(uid, credits);
      else if (kind === "ban") await adminApi.banAppUser(uid, !user.banned);
      else if (kind === "delete") await adminApi.deleteAppUser(uid);
      onDone();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[8px] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid={`app-user-action-modal-${kind}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-[color:var(--gv-ink)]">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[color:var(--gv-mist)]"><X size={16} /></button>
        </div>
        <div className="text-[13px] text-[color:var(--gv-ink)]/70 mb-4">{user.email}</div>

        {kind === "plan" && (
          <>
            <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Plan</label>
            <select className="gv-input w-full mb-3" value={planValue} onChange={(e) => setPlanValue(e.target.value)} data-testid="action-plan-select">
              <option value="free">Free</option>
              <option value="amateur">Amateur</option>
              <option value="pro">Pro</option>
            </select>
            <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Durée (mois)</label>
            <input type="number" min={1} max={36} value={months} onChange={(e) => setMonths(parseInt(e.target.value || "1", 10))} className="gv-input w-full mb-3" data-testid="action-plan-months" />
          </>
        )}
        {kind === "credits" && (
          <>
            <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Crédits à ajouter</label>
            <input type="number" min={1} value={credits} onChange={(e) => setCredits(parseInt(e.target.value || "0", 10))} className="gv-input w-full mb-3" data-testid="action-credits-amount" />
          </>
        )}
        {kind === "ban" && (
          <p className="text-sm text-[color:var(--gv-ink)]/75 mb-3">
            {user.banned
              ? "Cet utilisateur pourra à nouveau se connecter."
              : "L'utilisateur sera immédiatement déconnecté et ne pourra plus se connecter."}
          </p>
        )}
        {kind === "delete" && (
          <p className="text-sm text-[color:var(--gv-ink)]/75 mb-3">
            Cette action est <strong>irréversible</strong>. Le compte, son abonnement en cours,
            ses crédits et son historique seront supprimés côté app.geovinum.io.
          </p>
        )}

        {err && <div className="text-[12px] text-red-600 mb-2" data-testid="action-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px]">Annuler</button>
          <button
            onClick={submit}
            disabled={busy}
            className={`px-4 py-2 rounded-[4px] text-white text-[13px] disabled:opacity-50 ${
              kind === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-[color:var(--gv-ink)]"
            }`}
            data-testid="action-submit"
          >
            {busy ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}{" "}
            {kind === "delete" ? "Supprimer" : "Valider"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAppUsersPage;
